import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { Loader2, Lock, ArrowLeft } from "lucide-react";

const MASSAGE_LOGO = "https://customer-assets.emergentagent.com/job_elite-athlete-hub/artifacts/8vegkn7q_IMG_5565.png";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API}/admin/login`, {
          email: formData.email,
          password: formData.password
        });
        localStorage.setItem("adminToken", response.data.token);
        localStorage.setItem("adminUser", JSON.stringify(response.data.user));
        toast.success("Welcome back!");
        navigate("/admin/dashboard");
      } else {
        const response = await axios.post(`${API}/admin/register`, formData);
        localStorage.setItem("adminToken", response.data.token);
        localStorage.setItem("adminUser", JSON.stringify(response.data.user));
        toast.success("Account created successfully!");
        navigate("/admin/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={MASSAGE_LOGO} alt="Top Notch" className="h-24 mx-auto mb-4" />
          <h1 className="font-display text-3xl text-white">ADMIN PANEL</h1>
        </div>

        <Card className="bg-[#111111] border-[#2A2A2A]">
          <CardHeader>
            <CardTitle className="text-white text-center">
              {isLogin ? "Sign In" : "Create Account"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-auth-form">
              {!isLogin && (
                <div>
                  <Label htmlFor="name" className="text-gray-300">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="form-input mt-1"
                    placeholder="Your name"
                    data-testid="admin-name"
                    required={!isLogin}
                  />
                </div>
              )}
              <div>
                <Label htmlFor="email" className="text-gray-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="form-input mt-1"
                  placeholder="admin@example.com"
                  data-testid="admin-email"
                  required
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="form-input mt-1"
                  placeholder="••••••••"
                  data-testid="admin-password"
                  required
                />
              </div>

              <Button 
                type="submit" 
                className="w-full cta-btn-primary"
                disabled={loading}
                data-testid="admin-submit"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    {isLogin ? "SIGN IN" : "CREATE ACCOUNT"}
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-gray-400 hover:text-[#E10600] text-sm"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? "Need an account? Register" : "Already have an account? Sign in"}
              </button>
            </div>

            <div className="mt-4 text-center">
              <Link to="/" className="text-gray-500 hover:text-white text-sm flex items-center justify-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to website
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLoginPage;
